import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4011/api';

interface Country {
  code: string;
  name: string;
  flag: string;
  languages: string[];
}

interface CountrySelectorProps {
  accessToken: string | null;
  onComplete: () => void;
}

export const CountrySelector: React.FC<CountrySelectorProps> = ({
  accessToken,
  onComplete,
}) => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [matchAny, setMatchAny] = useState(true);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/countries`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setCountries(response.data);
    } catch (err) {
      setError('Failed to load countries');
    }
  };

  const toggleCountry = (code: string) => {
    if (selected.includes(code)) {
      setSelected(selected.filter((c) => c !== code));
      setError('');
    } else {
      if (selected.length < 10) {
        setSelected([...selected, code]);
        setError('');
      } else {
        setError('Maximum 10 countries allowed');
      }
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/users/me/countries`,
        { countries: selected, matchAny },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      onComplete();
    } catch (err) {
      setError('Failed to save country preferences');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Where would you like to connect?</h2>
      <p style={styles.subtitle}>
        Select countries to match with people from specific regions (optional)
      </p>

      <div style={styles.matchAnyContainer}>
        <label style={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={matchAny}
            onChange={(e) => {
              setMatchAny(e.target.checked);
              if (e.target.checked) {
                setSelected([]);
                setError('');
              }
            }}
            style={styles.checkbox}
          />
          <span style={styles.checkboxText}>Match with any country (no filter)</span>
        </label>
      </div>

      {!matchAny && (
        <>
          <div style={styles.counter}>
            {selected.length} / 10 countries selected
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <div style={styles.countryGrid}>
            {countries.map((country) => (
              <button
                key={country.code}
                onClick={() => toggleCountry(country.code)}
                style={{
                  ...styles.countryCard,
                  ...(selected.includes(country.code)
                    ? styles.countryCardSelected
                    : {}),
                }}
              >
                <span style={styles.flag}>{country.flag}</span>
                <span style={styles.countryName}>{country.name}</span>
                <span style={styles.languages}>
                  {country.languages.join(', ')}
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      <button
        onClick={handleSave}
        disabled={loading || (!matchAny && selected.length === 0)}
        style={{
          ...styles.continueButton,
          ...(loading || (!matchAny && selected.length === 0)
            ? styles.buttonDisabled
            : {}),
        }}
      >
        {loading ? 'Saving...' : 'Continue to Video Chat'}
      </button>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    width: '100%',
    marginTop: '2rem',
  },
  title: {
    color: 'var(--color-deep-purple)',
    fontSize: '1.8rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
    textAlign: 'center',
  },
  subtitle: {
    color: 'var(--color-gray-600)',
    textAlign: 'center',
    marginBottom: '1.5rem',
  },
  matchAnyContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '1.5rem',
    padding: '1rem',
    background: 'var(--color-gray-50)',
    borderRadius: 'var(--radius-md)',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    gap: '0.75rem',
  },
  checkbox: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
    accentColor: 'var(--color-warm-orange)',
  },
  checkboxText: {
    color: 'var(--color-dark-navy)',
    fontSize: '1rem',
    fontWeight: '500',
  },
  counter: {
    textAlign: 'center',
    fontSize: '1rem',
    fontWeight: 'bold',
    color: 'var(--color-deep-purple)',
    marginBottom: '1rem',
  },
  error: {
    background: '#fee',
    color: '#c33',
    padding: '0.75rem',
    borderRadius: 'var(--radius-md)',
    marginBottom: '1rem',
    textAlign: 'center',
    fontSize: '0.9rem',
  },
  countryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '1rem',
    maxHeight: '400px',
    overflowY: 'auto',
    marginBottom: '1.5rem',
    padding: '0.5rem',
  },
  countryCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '1rem',
    border: '2px solid var(--color-gray-300)',
    borderRadius: 'var(--radius-md)',
    background: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'center',
  },
  countryCardSelected: {
    background: 'var(--color-soft-blue)',
    borderColor: 'var(--color-deep-purple)',
    color: 'white',
  },
  flag: {
    fontSize: '2.5rem',
    marginBottom: '0.5rem',
  },
  countryName: {
    fontSize: '0.95rem',
    fontWeight: '600',
    marginBottom: '0.25rem',
  },
  languages: {
    fontSize: '0.75rem',
    opacity: 0.8,
  },
  continueButton: {
    width: '100%',
    background: 'var(--color-warm-orange)',
    color: 'white',
    padding: '0.75rem',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '1rem',
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
};
